import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Layout from '../components/Layout';
import {
  getMbcbNotificationSetting,
  updateMbcbNotificationSetting,
  previewMbcbNotificationRecipients,
  searchUsersForNotifications,
  MBCB_NOTIFICATION_EVENT_REPORT_SUBMITTED,
  MbcbNotificationSettingDto,
  MbcbNotificationUpdatePayload,
  NotificationUserSearchResult,
} from '../api/mbcbNotificationSettingsService';
import { planterService, ChurchPlanter } from '../api/planterService';
import { userService } from '../api/userService';

const ROLE_OPTIONS: { value: string; label: string }[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'staff', label: 'Staff' },
  { value: 'mbcb', label: 'MBCB' },
  { value: 'hub_leader', label: 'Hub leader' },
  { value: 'church_planter', label: 'Church planter' },
  { value: 'assessor', label: 'Assessor' },
  { value: 'coach', label: 'Coach' },
  { value: 'trainer', label: 'Trainer' },
  { value: 'pe_admin', label: 'PE admin' },
];

const MbcbNotificationSettings: React.FC = () => {
  const queryClient = useQueryClient();
  const eventType = MBCB_NOTIFICATION_EVENT_REPORT_SUBMITTED;

  const [enabled, setEnabled] = useState(true);
  const [includeAssignedHubLeader, setIncludeAssignedHubLeader] = useState(true);
  const [includeRoles, setIncludeRoles] = useState<string[]>(['admin', 'staff', 'mbcb']);
  const [additionalUserIds, setAdditionalUserIds] = useState<number[]>([]);
  const [additionalEmails, setAdditionalEmails] = useState<string[]>([]);
  const [userLabels, setUserLabels] = useState<Record<number, string>>({});

  const [userSearch, setUserSearch] = useState('');
  const [userHits, setUserHits] = useState<NotificationUserSearchResult[]>([]);
  const [userSearchOpen, setUserSearchOpen] = useState(false);

  const [emailInput, setEmailInput] = useState('');
  const [previewPlanterId, setPreviewPlanterId] = useState<number | ''>('');
  const [previewRecipients, setPreviewRecipients] = useState<string[] | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const { data: settings, isLoading } = useQuery({
    queryKey: ['mbcbNotificationSettings', eventType],
    queryFn: () => getMbcbNotificationSetting(eventType),
  });

  const { data: plantersData } = useQuery({
    queryKey: ['churchPlanters', 'mbcb-notify-preview'],
    queryFn: () => planterService.getAllPlanters({ page: 1, limit: 500 }),
  });
  const planters: ChurchPlanter[] = plantersData?.data ?? [];

  const applyDto = useCallback(async (dto: MbcbNotificationSettingDto) => {
    setEnabled(dto.enabled);
    setIncludeAssignedHubLeader(dto.includeAssignedHubLeader);
    setIncludeRoles(dto.includeRoles?.length ? [...dto.includeRoles] : ['admin', 'staff', 'mbcb']);
    setAdditionalUserIds(dto.additionalUserIds ?? []);
    setAdditionalEmails(dto.additionalEmails ?? []);

    const ids = dto.additionalUserIds ?? [];
    const labels: Record<number, string> = {};
    await Promise.all(
      ids.map(async (id) => {
        try {
          const res = (await userService.getUser(id)) as {
            data?: { firstName?: string; lastName?: string; email?: string };
          };
          const u = res.data;
          if (u) {
            labels[id] = `${u.firstName ?? ''} ${u.lastName ?? ''} (${u.email})`.trim();
          }
        } catch {
          labels[id] = `User #${id}`;
        }
      }),
    );
    setUserLabels(labels);
  }, []);

  useEffect(() => {
    if (settings) {
      applyDto(settings);
    }
  }, [settings, applyDto]);

  const draftPayload = useMemo((): MbcbNotificationUpdatePayload => ({
    enabled,
    includeAssignedHubLeader,
    includeRoles,
    additionalUserIds,
    additionalEmails,
  }), [enabled, includeAssignedHubLeader, includeRoles, additionalUserIds, additionalEmails]);

  const saveMutation = useMutation({
    mutationFn: () => updateMbcbNotificationSetting(eventType, draftPayload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['mbcbNotificationSettings', eventType] });
      setPreviewRecipients(null);
    },
  });

  const toggleRole = (role: string) => {
    setIncludeRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );
  };

  const addUser = (u: NotificationUserSearchResult) => {
    if (additionalUserIds.includes(u.id)) return;
    setAdditionalUserIds((prev) => [...prev, u.id]);
    setUserLabels((prev) => ({
      ...prev,
      [u.id]: `${u.firstName} ${u.lastName} (${u.email})`,
    }));
    setUserSearch('');
    setUserHits([]);
    setUserSearchOpen(false);
  };

  const removeUser = (id: number) => {
    setAdditionalUserIds((prev) => prev.filter((x) => x !== id));
    setUserLabels((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const addEmailFromInput = () => {
    const raw = emailInput.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const next = [...additionalEmails];
    for (const e of raw) {
      if (emailRegex.test(e) && !next.includes(e.toLowerCase())) {
        next.push(e.toLowerCase());
      }
    }
    setAdditionalEmails(next);
    setEmailInput('');
  };

  const removeEmail = (e: string) => {
    setAdditionalEmails((prev) => prev.filter((x) => x !== e));
  };

  useEffect(() => {
    if (!userSearchOpen || userSearch.trim().length < 1) {
      setUserHits([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const hits = await searchUsersForNotifications(userSearch.trim(), 25);
        setUserHits(hits);
      } catch {
        setUserHits([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [userSearch, userSearchOpen]);

  const runPreview = async () => {
    setPreviewError(null);
    try {
      const data = await previewMbcbNotificationRecipients(eventType, {
        planterId: previewPlanterId === '' ? null : Number(previewPlanterId),
        draft: draftPayload,
      });
      setPreviewRecipients(data.recipients);
    } catch (e: unknown) {
      const msg = e && typeof e === 'object' && 'response' in e
        ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
        : null;
      setPreviewError(msg || 'Preview failed');
      setPreviewRecipients(null);
    }
  };

  if (isLoading && !settings) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold text-gray-900">MBCB report submitted — email recipients</h1>
        <p className="mt-2 text-sm text-gray-600">
          When a planter submits an MBCB monthly report, notification emails go to the addresses below.
          Default matches previous behavior: assigned hub leader plus users with Admin, Staff, and MBCB roles.
        </p>

        <div className="mt-8 space-y-8 bg-white shadow rounded-lg p-6">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            <span className="font-medium text-gray-900">Enable submit notification emails</span>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              checked={includeAssignedHubLeader}
              onChange={(e) => setIncludeAssignedHubLeader(e.target.checked)}
              disabled={!enabled}
            />
            <span className={`font-medium ${!enabled ? 'text-gray-400' : 'text-gray-900'}`}>
              Include the planter&apos;s assigned hub leader
            </span>
          </label>

          <div>
            <p className="text-sm font-medium text-gray-900 mb-2">Also notify users with these roles</p>
            <div className="flex flex-wrap gap-2">
              {ROLE_OPTIONS.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  disabled={!enabled}
                  onClick={() => toggleRole(r.value)}
                  className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                    includeRoles.includes(r.value)
                      ? 'bg-primary-100 border-primary-500 text-primary-800'
                      : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                  } ${!enabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-sm font-medium text-gray-900 mb-2">Additional users (by account)</p>
            <div className="relative">
              <input
                type="text"
                disabled={!enabled}
                placeholder="Search by name or email…"
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                value={userSearch}
                onChange={(e) => {
                  setUserSearch(e.target.value);
                  setUserSearchOpen(true);
                }}
                onFocus={() => setUserSearchOpen(true)}
              />
              {userSearchOpen && userHits.length > 0 && (
                <ul className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5">
                  {userHits.map((u) => (
                    <li key={u.id}>
                      <button
                        type="button"
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                        onClick={() => addUser(u)}
                      >
                        {u.firstName} {u.lastName}{' '}
                        <span className="text-gray-500">{u.email}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {additionalUserIds.map((id) => (
                <span
                  key={id}
                  className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-800"
                >
                  {userLabels[id] || `User #${id}`}
                  <button
                    type="button"
                    className="text-gray-500 hover:text-red-600"
                    onClick={() => removeUser(id)}
                    aria-label={`Remove user ${id}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div>
            <p className="text-sm font-medium text-gray-900 mb-2">Additional email addresses</p>
            <div className="flex gap-2">
              <input
                type="text"
                disabled={!enabled}
                placeholder="email@example.com"
                className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addEmailFromInput();
                  }
                }}
              />
              <button
                type="button"
                disabled={!enabled}
                onClick={addEmailFromInput}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
              >
                Add
              </button>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {additionalEmails.map((e) => (
                <span
                  key={e}
                  className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-sm"
                >
                  {e}
                  <button
                    type="button"
                    className="text-gray-500 hover:text-red-600"
                    onClick={() => removeEmail(e)}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
            >
              {saveMutation.isPending ? 'Saving…' : 'Save settings'}
            </button>
            {saveMutation.isSuccess && (
              <span className="text-sm text-green-600">Saved.</span>
            )}
            {saveMutation.isError && (
              <span className="text-sm text-red-600">Save failed.</span>
            )}
          </div>
        </div>

        <div className="mt-8 max-w-4xl mx-auto bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900">Preview recipients</h2>
          <p className="mt-1 text-sm text-gray-600">
            Optionally pick a church planter to include their assigned hub leader in the preview.
          </p>
          <div className="mt-4 flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500">Planter (optional)</label>
              <select
                className="mt-1 block w-64 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                value={previewPlanterId === '' ? '' : String(previewPlanterId)}
                onChange={(e) =>
                  setPreviewPlanterId(e.target.value === '' ? '' : parseInt(e.target.value, 10))
                }
              >
                <option value="">None (hub leader omitted)</option>
                {planters.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.churchName || p.name} (#{p.id})
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={runPreview}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Preview with current form
            </button>
          </div>
          {previewError && <p className="mt-2 text-sm text-red-600">{previewError}</p>}
          {previewRecipients && (
            <ul className="mt-4 list-disc list-inside text-sm text-gray-800 space-y-1">
              {previewRecipients.length === 0 ? (
                <li className="text-gray-500">No recipients (notifications disabled or no matching emails).</li>
              ) : (
                previewRecipients.map((r) => <li key={r}>{r}</li>)
              )}
            </ul>
          )}
        </div>

        <p className="mt-6 text-xs text-gray-500">
          Event key: <code className="bg-gray-100 px-1 rounded">{eventType}</code>
        </p>
      </div>
    </Layout>
  );
};

export default MbcbNotificationSettings;
